/**
 * MFA_Challenge_Screen.tsx — 6-digit MFA step after login returns
 * { kind: 'mfa_required', mfa_token }. Styled to match the SK Platform auth card
 * (same logo/tagline/card chrome) for a consistent auth flow. Calls
 * services/auth completeMfa, then enters the app on success.
 */

import React, { useState } from 'react';
import { View, Text, StyleSheet, ScrollView, Platform, TextInput, KeyboardAvoidingView } from 'react-native';

import PrimaryButton from '../../components/primaryButton';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AuthStackParamList } from '../../navigation/types';
import { CONTENT } from '../../utils/responsive';
import { useAuthFlow } from '../../navigation/authFlowContext';
import { takePendingInvite } from '../../navigation/pendingInvite';
import { completeMfa } from '../../services/auth/auth';
import { handleApiError } from '../../utils/errorHandler';
import { showSuccess } from '../../utils/successHandler';
import { useTheme } from '../../context/themeContext';
import AuthBrandHeader from '../../components/authBrandHeader';

const MfaChallenge_Screen: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<AuthStackParamList, 'mfaChallenge'>>();
  const route = useRoute();
  const { theme } = useTheme();
  const { colors } = theme;
  const { enterApp } = useAuthFlow();
  const mfaToken = (route.params as any)?.mfaToken ?? '';
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const canSubmit = code.replace(/\s/g, '').length === 6 && !loading;

  const verify = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      const res = await completeMfa(mfaToken, code);
      showSuccess(res, 'Verified', 'Welcome back!');
      // The other exit that reaches the shell. An MFA-protected account taking the
      // invite path would otherwise land in their dashboard with the invitation
      // still unaccepted — see navigation/pendingInvite for why this is module
      // state and not a route param threaded through both screens.
      const pendingInvite = takePendingInvite();
      if (pendingInvite) {
        navigation.navigate('invitationAccept', { token: pendingInvite });
        return;
      }
      enterApp();
    } catch (err) {
      handleApiError(err, 'Verification failed', 'That code did not match. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={[styles.root, { backgroundColor: colors.paper }]}>
      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
        <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
          <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.line }]}>
            <AuthBrandHeader />
            <Text style={[styles.title, { color: colors.ink }]}>Two-factor authentication</Text>
            <Text style={[styles.sub, { color: colors.ink2 }]}>Enter the 6-digit code from your authenticator app.</Text>

            <TextInput
              value={code}
              onChangeText={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              placeholderTextColor={colors.ink4}
              keyboardType="number-pad"
              maxLength={6}
              style={[styles.codeInput, { borderColor: colors.line, color: colors.ink, backgroundColor: colors.card }]}
            />

            <PrimaryButton title="Verify  →" onPress={verify} disabled={!canSubmit} loading={loading} hoverLift style={styles.submit} textStyle={styles.submitText} />

            <View style={styles.footer}>
              <PrimaryButton title="Back to sign in" onPress={() => navigation.navigate('login')} variant="ghost" size="small" textStyle={{ color: colors.accent }} />
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  root: { flex: 1, ...(Platform.OS === 'web' ? ({ minHeight: '100vh' } as any) : {}) },
  scrollContent: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', paddingVertical: 40, paddingHorizontal: 16 },
  card: {
    width: '100%', maxWidth: CONTENT.form, borderRadius: 24, padding: 32, borderWidth: 1, overflow: 'hidden',
    ...(Platform.OS === 'web' ? ({ boxShadow: '0 24px 60px -18px rgba(15,23,42,0.22)' } as any) : { shadowColor: '#0F172A', shadowOffset: { width: 0, height: 14 }, shadowOpacity: 0.14, shadowRadius: 32, elevation: 10 }),
  },
  accentBar: { position: 'absolute', top: 0, left: 0, right: 0, height: 4 },
  logoRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginBottom: 18, gap: 10 },
  logoMark: { width: 38, height: 38, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  logoText: { fontWeight: '800', fontSize: 15, letterSpacing: -0.5 },
  logoName: { fontSize: 19, fontWeight: '800', letterSpacing: -0.4 },
  title: { fontSize: 26, fontWeight: '900', marginBottom: 8, textAlign: 'center', letterSpacing: -0.8 },
  sub: { fontSize: 14, fontWeight: '500', marginBottom: 22, textAlign: 'center', lineHeight: 22 },
  codeInput: {
    height: 52, borderWidth: 1, borderRadius: 14, textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: '700',
    ...(Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : {}),
  },
  submit: { marginTop: 16, height: 52, minHeight: 52, borderRadius: 14 },
  submitText: { fontSize: 15, letterSpacing: 1.2 },
  footer: { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginTop: 16 },
});

export default MfaChallenge_Screen;
